<template>
  <div>
    <div class="registryForm">
      <vt-side-panel
        v-if="toggleSidepanel && requestLabel"
        :visible="toggleSidepanel"
      >
        <study-request-detail
          :cas-id="casId"
          :lab-id="labId"
        >
          <b-button
            id="requestsSidePanelClose"
            slot="buttons-end"
            v-shortkey="['shift', 'esc']"
            class="btn__icon btn__icon--primary pl-3 pr-0 py-0"
            @shortkey.prevent="closeRequestSidePanel()"
            @click="closeRequestSidePanel()"
          >
            <i
              class="fal fa-times"
              aria-hidden="true"
            />
          </b-button>
        </study-request-detail>
      </vt-side-panel>
      <!--Search and help block-->
      <b-row class="search-block  mb-2 mt-1">
        <b-col
          xl="8"
          lg="7"
          md="7"
          sm="12"
          class="d-flex"
        >
          <searcher-module-entity-selection
            ref="searcherModule"
            :search-patient-with-name="false"
            :identifier-options="entitySelectorOptionsForSearch"
            :initial-identifier="entitySelectorOptionsForSearch[0]"
            class="w-sm-100 mb-sm-2"
            @sampleSearch="onSampleSearch($event)"
            @studySearch="onStudySearch($event)"
            @patientSearch="onPatientSearch($event)"
          />
        </b-col>
        <b-col
          xl="4"
          lg="5"
          md="5"
          sm="12"
          class="text-right d-flex align-items-center"
        >
          <div class="w-100">
            <b-button
              qa="case-index-reports-button"
              class="btn__link px-3"
              @click="openBatchReportsModal"
            >
              <i
                class="far fa-copy mr-2"
                aria-hidden="true"
              />{{ $tc('registry.btn.reports') }}
            </b-button>
            <b-button
              v-shortkey="['f10']"
              qa="case-index-patients-button"
              class="btn__link px-3"
              @shortkey.prevent="openPatientModal"
              @click="openPatientModal"
            >
              <i
                class="far fa-users mr-2"
                aria-hidden="true"
              />{{ $tc('global.patients') }}
            </b-button>
            <b-button
              v-if="$_canCreateCase()"
              id="requestsRouteToStudyRequestCreate"
              v-shortkey="['alt', 'n']"
              class="mx-3 btn btn--primary btn-secondary btn-sm my-md-2"
              qa="case-index-create-case-button"
              @click="openCreateCaseModal(false)"
              @shortkey.prevent="openCreateCaseModal(false)"
            >
              <fa-icon
                class="mr-1"
                :icon="['fal', 'plus']"
              /> {{ $tc('global.case',1) }}
            </b-button>
            <b-button
              v-tooltip="$tc('global.statusOptions.incidence',2)"
              qa="case-index-incidents-button"
              class="round-btn round-btn--incidents mr-0 my-md-2"
              @click="openIncidentModal(null)"
            >
              <i
                class="fal fa-exclamation-triangle"
                aria-hidden="true"
              />
            </b-button>
            <b-button
              v-tooltip="$tc('global.file',2)"
              qa="case-index-attachments-button"
              class="round-btn round-btn--attachments ml-2 mr-1 my-md-2"
              @click="showPatientAttachmentTreeModal"
            >
              <i
                class="fal fa-paperclip"
                aria-hidden="true"
              />
            </b-button>
          </div>
        </b-col>
      </b-row>

      <!--Patient Block-->
      <div class="entity-card">
        <b-row class="py-2 px-3">
          <b-col
            cols="auto"
            class="d-flex align-items-center"
          >
            <b-button
              class="btn__icon btn__icon--primary"
              @click="showModal"
            >
              <i
                class="far fa-user-circle mr-2 main"
                aria-hidden="true"
              />
            </b-button>
            <span
              v-if="patientInformation"
              class="entity-card__title d-flex align-items-center font-weight--medium"
              qa="case-index-patient-card-fullname"
            >
              {{ patientInformation.fullName }}
            </span>
            <vtBubble
              v-if="patientIncidentsCount"
              :number="patientIncidentsCount"
              :type="true"
              :tooltip="` ${$tc('grid.detail.hasAlarms')}`"
              class="ml-1"
              qa="case-index-patient-card-incidents-bubble"
              @click="openIncidentModal(null)"
            />
            <vtBubble
              v-if="patientAttachmentsCount"
              :number="patientAttachmentsCount"
              :type="false"
              :tooltip="` ${$tc('global.file', 2)}`"
              qa="case-index-patient-card-attachments-bubble"
              @click="showPatientAttachmentTreeModal"
            />
          </b-col>
          <b-col class="d-flex align-items-center justify-content-end">
            <b-button
              v-tooltip="$tc('registry.cases.showPanel')"
              qa="case-index-patient-card-view-previous-cases-panel-button"
              class="btn__icon btn__icon--primary mx-1"
              @click="activePreviousCases"
            >
              <i
                v-show="!showPreviousCases"
                class="far fa-eye"
                aria-hidden="true"
              />
              <i
                v-show="showPreviousCases"
                class="far fa-eye-slash"
                aria-hidden="true"
              />
            </b-button>
            <b-button
              id="requestsCleanFilters"
              v-tooltip="$tc('registry.cases.patientCases')"
              qa="case-index-patient-card-open-previous-cases-button"
              style="position:relative"
              class="btn__icon btn__icon--primary mx-1"
              @click="openPatientPreviousCases"
            >
              <i
                class="fas fa-history"
                aria-hidden="true"
              />
              <div
                v-if="patientPreviousCasesCountByLaboratory"
                class="btn-badge"
              >
                <span class="badge vt-nav-badge indicator-bubble badge-secondary">{{ patientPreviousCasesCountByLaboratory }}</span>
              </div>
            </b-button>
            <vt-loading-button
              v-if="$_canPrintPreviousCaseReport()"
              id="printCases"
              v-tooltip="$tc('registry.cases.printCases')"
              qa="case-index-patient-card-print-cases-button"
              class="btn__icon btn__icon--primary mx-1"
              :params="{ promise: exportPatientPreviousCasesReportPDF }"
            >
              <i
                class="fas fa-print"
                aria-hidden="true"
              />
            </vt-loading-button>
            <b-button
              id="patient-card-pop"
              qa="case-index-patient-card-ellipsis-button"
              class="btn__icon btn__icon--primary mx-1"
            >
              <i
                class="fas fa-ellipsis-v"
                aria-hidden="true"
              />
            </b-button>
            <b-popover
              target="patient-card-pop"
              placement="bottom"
              triggers="click blur"
            >
              <div class="ap-popover">
                <ul>
                  <li
                    v-if="$_canCreateCase()"
                    qa="case-index-patient-card-create-new-case-button"
                  >
                    <b-button
                      @click="createNewCase(patientInformation)"
                    >
                      {{ $tc('registry.cases.newCase') }}
                    </b-button>
                  </li>
                  <li
                    v-if="$_canEditPatient()"
                    qa="case-index-patient-card-edit-patient-button"
                  >
                    <b-button
                      @click="openEditPatientModal(false)"
                    >
                      {{ $tc('global.editPatient') }}
                    </b-button>
                  </li>
                  <li
                    v-if="$_canDeletePatient()"
                    qa="case-index-patient-card-delete-patient-button"
                  >
                    <b-button
                      @click="deletePatient(patientInformation.patId)"
                    >
                      {{ $tc('global.deletePatient') }}
                    </b-button>
                  </li>
                  <li
                    v-if="$_canMergePatient()"
                    qa="case-index-patient-card-patient-merge-button"
                  >
                    <b-button
                      @click="openModalPatientFusion"
                    >
                      {{ $tc('component.patientFusion.title') }}
                    </b-button>
                  </li>
                  <div class="horizontal-divider my-1" />
                  <li
                    v-if="$_canCreateIncident()"
                    qa="case-index-patient-card-add-incident-button"
                  >
                    <b-button
                      @click="openIncidentFormModal('PATIENT', {patId: patientInformation.patId})"
                    >
                      {{ $tc('registry.requestGrid.contextMenu.registerIncident') }}
                    </b-button>
                  </li>
                  <li
                    v-if="$_canAttachDocumentToPatient()"
                    qa="case-index-patient-card-add-attachment-button"
                  >
                    <b-button
                      :disabled="!$_canBeDocumentAttachedToPatient(patientInformation)"
                      @click="showPatientAttachmentModal"
                    >
                      {{ $tc('registry.btn.addAttach') }}
                    </b-button>
                  </li>
                </ul>
              </div>
            </b-popover>
          </b-col>
        </b-row>
        <div class="horizontal-divider" />
        <b-row class="py-2 px-4">
          <b-col
            v-if="globalParameters.mainPatientIdentifierType"
            class="entity-card__data"
          >
            <span class="entity-card__data--label">{{ globalParameters.mainPatientIdentifierType.name }}</span>
            <span
              v-if="patientInformation"
              class="entity-card__data--text"
              qa="case-index-patient-card-main-identifier"
            >{{ patientInformation.mainPatientIdentifier ? patientInformation.mainPatientIdentifier.paiIdentifier : '-' }}</span>
          </b-col>
          <b-col
            v-if="globalParameters.secondaryPatientIdentifierType"
            class="entity-card__data"
          >
            <span class="entity-card__data--label">{{ globalParameters.secondaryPatientIdentifierType.name }}</span>
            <span
              v-if="patientInformation"
              class="entity-card__data--text"
              qa="case-index-patient-card-secondary-identifier"
            >{{ patientInformation.secondaryPatientIdentifier ? patientInformation.secondaryPatientIdentifier.paiIdentifier : '-' }}</span>
          </b-col>
          <b-col class="entity-card__data">
            <span class="entity-card__data--label">{{ $tc('entities.patient.gender') }}</span>
            <span
              v-if="patientInformation"
              class="entity-card__data--text"
              qa="case-index-patient-card-gender"
            >{{ patientInformation.genderName }}</span>
          </b-col>
          <b-col class="entity-card__data">
            <span class="entity-card__data--label">{{ $tc('entities.patient.birthdate') }}</span>
            <span
              v-if="patientInformation"
              class="entity-card__data--text"
              qa="case-index-patient-card-birthdate"
            >{{ patientInformation.patBirthdate | date }}</span>
          </b-col>
          <b-col class="entity-card__data">
            <span class="entity-card__data--label">{{ $tc('global.age') }}</span>
            <span
              class="entity-card__data--text"
              qa="case-index-patient-card-age"
            >{{ patientTimeOld }}</span>
          </b-col>
        </b-row>
      </div>
      <b-container
        fluid
        class="mt-2"
      >
        <b-row
          v-if="patientInformation"
          class="registry-card float-component"
        >
          <b-col
            v-show="showPreviousCases"
            class="previous-cases-column bordered-column-right"
            cols="3"
          >
            <patient-previous-cases
              v-if="patientPreviousCases"
              :cas-id="casId"
              :previous-cases="patientPreviousCases"
            />
          </b-col>
          <b-col class="p-0">
            <b-card
              header-tag="header"
              footer-tag="footer"
            >
              <b-row class="main-row">
                <b-col class="main-col">
                  <b-row class="case-header-section py-2">
                    <!-- Case number header -->
                    <b-col
                      class="d-flex align-items-center"
                      cols="auto"
                    >
                      <div class="d-flex align-items-center mb-0 mr-2">
                        <i
                          class="far fa-chart-network primary-dark-color mr-3"
                          aria-hidden="true"
                        />
                        <span
                          v-if="visibleReadOnlyCaseNumber"
                          qa="case-index-case-label-h5"
                          class="ap-title ap-title__main"
                        >
                          {{ caseNumber ? caseNumber : $tc('registry.cases.newCase') }}
                          <i
                            v-if="visibleHasUnauthorizedBillableConceptsIcon"
                            v-tooltip="$t('global.hasUnauthorizedBillableConcepts')"
                            aria-hidden="true"
                            class="ml-1 mr-1 fas fa-calculator danger-color"
                          />
                        </span>
                        <b-input-group
                          v-else
                          size="sm"
                          class="printed-label"
                        >
                          <vt-input
                            v-model="caseNumber"
                            v-focus
                            qa="case-index-case-input"
                            :placeholder="$tc('global.enterIdentifier') + '...'"
                          />
                        </b-input-group>
                      </div>
                      <div class="d-flex align-items-center mr-2">
                        <b-badge
                          v-if="$refs.caseForm && $refs.caseForm.formData.caseOfReference"
                          v-tooltip="$tc('registry.btn.consultation')"
                          pill
                          class="ml-1 icon-badge indicator-bubble d-flex align-items-center justify-content-center"
                        >
                          <i
                            class="far fa-glasses"
                            aria-hidden="true"
                          />
                        </b-badge>
                        <!-- TODO: Report read by healthcare professional indicator, uncomment when developed -->
                        <!--<b-badge v-tooltip="$tc('global.reportReadByHealthcareProfessional')" pill class="ml-1 is-cursor-pointer icon-badge indicator-bubble d-flex align-items-center justify-content-center">
                                                    <span class="far fa-stethoscope"></span>
                                                </b-badge>-->
                        <vtBubble
                          v-if="caseIncidentsCount"
                          qa="case-index-case-incidents-bubble"
                          :number="caseIncidentsCount"
                          :type="true"
                          :tooltip="` ${$tc('grid.detail.hasAlarms')}`"
                          class="ml-1"
                          @click="openIncidentModal({coreEntity: 'CASE', casId: casId})"
                        />
                        <vtBubble
                          v-if="caseAttachmentsCount"
                          qa="case-index-case-attachments-bubble"
                          :number="caseAttachmentsCount"
                          :type="false"
                          :tooltip="` ${$tc('global.file', 2)}`"
                          @click="showCaseAttachmentTreeModal"
                        />
                      </div>
                      <case-status-badge
                        v-if="caseStatus != null"
                        class="status-badge"
                        :status="caseStatus"
                      />
                      <b-badge
                        v-if="caseData && caseData.lastAmendmentDate"
                        v-tooltip="$tc('global.amendedCase')"
                        pill
                        class="mx-2 icon-badge indicator-bubble d-flex align-items-center justify-content-center"
                      >
                        <i
                          class="far fa-money-check-edit"
                          aria-hidden="true"
                        />
                      </b-badge>
                    </b-col>
                    <!-- Button section header -->
                    <b-col class="d-flex align-items-center justify-content-end">
                      <div class="switch-btn p-0 d-flex align-items-center">
                        <small class="mr-2">{{ $tc('global.scanPrintedLabel') }}</small>
                        <input
                          id="toggle-clinical-label"
                          v-model="printedLabel"
                          type="checkbox"
                          class="offscreen"
                          :disabled="disabledPrintedLabelButtonSwitch"
                          :value="printedLabel"
                          aria-hidden="true"
                          @change="saveSwitchExternalLabelState"
                        >
                        <label
                          :qa="`case-index-print-label-switch-${printedLabel}-label`"
                          for="toggle-clinical-label"
                          class="switch mb-0"
                        />
                      </div>
                      <!-- TODO Front: v-if false for next buttons until Billing development -->
                      <b-button
                        v-if="visibleConceptModal"
                        qa="case-index-accounting-modal-concepts-button"
                        class="btn__icon btn__icon--primary ml-3"
                        @click="showValuableConceptsModal"
                      >
                        <i
                          v-tooltip="$tc('component.valuableConceptsModal.valuableConcept', 2)"
                          class="far fa-calculator"
                          aria-hidden="true"
                        />
                      </b-button>
                      <b-button
                        v-if="false"
                        qa="case-index-accounting-activity-concepts-button"
                        class="btn__icon btn__icon--primary mx-2"
                        @click="showActivityConceptsModal"
                      >
                        <i
                          v-tooltip="$tc('component.valuableConceptsModal.serviceActivityConcepts')"
                          class="far fa-user-clock"
                          aria-hidden="true"
                        />
                      </b-button>
                    </b-col>
                    <b-col
                      v-if="printedLabel"
                      cols="12"
                    >
                      <alert-banner
                        :show="printedLabel"
                        variant="warning"
                        class="mt-2 mb-0"
                        close
                        :text="$tc('registry.alertBanner.printedLabel')"
                      />
                    </b-col>
                  </b-row>
                  <b-row class="case-content-section">
                    <b-col class="p-0">
                      <b-tabs
                        ref="caseFormTabs"
                        v-model="tabIndex"
                        v-shortkey="{left: ['ctrl','arrowleft'], right: ['ctrl','arrowright']}"
                        no-fade
                        pills
                        card
                        vertical
                        class="vt-tabs--vertical samples-tabs"
                        :class="{'banner-height': printedLabel}"
                        @shortkey.native="handleCaseTabsNavigation"
                      >
                        <b-tab
                          ref="caseTab"
                          class="pt-2"
                          :title-link-class="{ hasErrors: $refs.caseForm && $refs.caseForm.showErrors }"
                        >
                          <template slot="title">
                            <span
                              qa="case-index-case-tab-span"
                              class="fas fa-align-left"
                            />
                          </template>
                          <div class="section-content px-3">
                            <case-form
                              v-if="patientInformation"
                              ref="caseForm"
                              v-shortkey="['ctrl', '2']"
                              :case-data="caseData"
                              :patient-information="patientInformation"
                              :printed-label="printedLabel"
                              :case-external-label="caseNumber"
                              :show-previous-cases="showPreviousCases"
                              @printCaseLabel="onCaseFormPrintCaseLabel"
                              @shortkey.native="tabIndex = 1"
                              @openIncidentFormModal="openIncidentFormModal($event.coreEntity, $event.entitiesIds)"
                              @formUpdated="updateLastModified($event)"
                              @samplesUpdated="reloadSamples"
                              @loadSampleTypes="loadSampleTypes"
                              @mapCaseData="mapData($event)"
                              @case-attachment-added="loadCase(casId)"
                              @loadCase="loadCase(casId)"
                            />
                          </div>
                        </b-tab>

                        <b-tab
                          ref="sampleTab"
                          class="pt-2"
                          :disabled="$refs.caseForm && !$refs.caseForm.formData.caseType"
                          :title-link-class="{ hasErrors: $refs.caseSamples && $refs.caseSamples.showErrors }"
                        >
                          <template slot="title">
                            <div class="tab-bubble">
                              <span
                                qa="case-index-sample-tab-span"
                                class="far fa-prescription-bottle"
                              />
                              <div
                                qa="case-index-samples-count-div"
                                class="btn-badge"
                              >
                                <span class="badge vt-nav-badge indicator-bubble badge-secondary">
                                  {{ samplesCount }}
                                </span>
                              </div>
                            </div>
                          </template>
                          <case-samples
                            ref="caseSamples"
                            v-shortkey="['ctrl', '1']"
                            :case-study="caseData"
                            :case-type-id="caseTypeId"
                            :patient="patientInformation"
                            :tab-index="tabIndex"
                            @shortkey.native="tabIndex = 0"
                            @printSampleLabels="onCaseSamplesPrintSampleLabels"
                            @load-case="loadCase(casId)"
                            @load-samples="onLoadedSamples($event)"
                            @openIncidentFormModal="openIncidentFormModal($event.coreEntity, $event.entitiesIds)"
                            @openIncidentModal="onOpenSampleIncidentModal($event)"
                          />
                        </b-tab>

                        <template #tabs-end>
                          <div class="vt-tabs--vertical vt-tabs--vertical__footer">
                            <ul>
                              <li
                                v-if="requestLabel"
                                class="active d-flex align-items-center justify-content-center"
                              >
                                <b-button
                                  qa="case-index-request-sidepanel-button"
                                  class="btn__icon btn__icon--primary"
                                  @click="toggleRequestSidePanel()"
                                >
                                  <i
                                    v-tooltip="{ title: $tc('global.requestInformation'), placement: 'right', boundary: 'viewport' }"
                                    class="far fa-file-medical"
                                    aria-hidden="true"
                                  />
                                </b-button>
                              </li>
                              <li class="active d-flex align-items-center justify-content-center">
                                <b-button
                                  qa="case-index-associated-cases-button"
                                  class="btn__icon btn__icon--primary"
                                  :disabled="disabledHtmlAssociatedCasesButton"
                                  style="position:relative"
                                  @click="openModalAssociatedCases"
                                >
                                  <i
                                    v-tooltip="{ title: $tc('registry.btn.associatedCases'), placement: 'right', boundary: 'viewport' }"
                                    class="far fa-exchange-alt"
                                    aria-hidden="true"
                                  />
                                  <div
                                    v-if="associatedCases.length"
                                    class="btn-badge"
                                  >
                                    <span class="badge vt-nav-badge indicator-bubble badge-secondary">{{ associatedCases.length }}</span>
                                  </div>
                                </b-button>
                              </li>
                              <li
                                v-if="$_canReassignPatientToCase()"
                                class="active d-flex align-items-center justify-content-center"
                              >
                                <b-button
                                  qa="case-index-reassign-patient-button"
                                  class="btn__icon btn__icon--primary"
                                  :disabled="disableReassingPatient"
                                  @click="openCreateCaseModal(true)"
                                >
                                  <i
                                    v-tooltip="{ title: $tc('registry.btn.reassignPatient'), placement: 'right', boundary: 'viewport' }"
                                    class="far fa-user-friends"
                                    aria-hidden="true"
                                  />
                                </b-button>
                              </li>
                              <div
                                v-if="$_canPrintCaseLabels() || $_canPrintCaseReport()"
                                class="tab-horizontal-divider"
                              />
                              <template v-if="$_canPrintCaseLabels()">
                                <li class="active d-flex align-items-center justify-content-center">
                                  <b-button
                                    qa="case-index-custom-print-button"
                                    class="btn__icon btn__icon--primary"
                                    :disabled="!$_canBeCaseLabelsPrinted(caseData)"
                                    @click="openCustomPrintModal"
                                  >
                                    <i
                                      v-tooltip="{ title: $tc('registry.btn.printCustomLabels'), placement: 'right', boundary: 'viewport' }"
                                      class="icon-ticket-setup"
                                      aria-hidden="true"
                                    />
                                  </b-button>
                                </li>
                                <li class="active d-flex align-items-center justify-content-center">
                                  <b-button
                                    qa="case-index-print-labels-case-button"
                                    class="btn__icon btn__icon--primary"
                                    :disabled="!$_canBeCaseLabelsPrinted(caseData)"
                                    @click="onPrintLabelsCaseButtonClick"
                                  >
                                    <i
                                      v-tooltip="{ title: $tc('registry.btn.printLabelsCaseSample'), placement: 'right', boundary: 'viewport' }"
                                      class="far fa-ticket-alt"
                                      aria-hidden="true"
                                    />
                                  </b-button>
                                </li>
                              </template>

                              <template v-if="$_canPrintCaseReport()">
                                <div class="tab-horizontal-divider" />
                                <li class="active d-flex align-items-center justify-content-center">
                                  <vt-loading-button
                                    qa="case-index-print-report-button"
                                    class="btn__icon btn__icon--primary"
                                    :disabled="!canViewReport"
                                    :params="{ promise: printCaseAPReport }"
                                  >
                                    <i
                                      v-tooltip="{ title: $tc('registry.btn.printAnaReport'), placement: 'right', boundary: 'viewport' }"
                                      class="far fa-file-contract"
                                      aria-hidden="true"
                                    />
                                  </vt-loading-button>
                                </li>
                                <li class="active d-flex align-items-center justify-content-center">
                                  <vt-loading-button
                                    qa="case-index-view-report-button"
                                    class="btn__icon btn__icon--primary"
                                    :disabled="!canViewReport"
                                    :params="{ promise: getCaseReportPDF }"
                                  >
                                    <i
                                      v-tooltip="{ title: $tc('registry.btn.viewAnaReport'), placement: 'right', boundary: 'viewport' }"
                                      class="far fa-file-upload"
                                      aria-hidden="true"
                                    />
                                  </vt-loading-button>
                                </li>
                              </template>
                            </ul>
                          </div>
                        </template>
                      </b-tabs>
                    </b-col>
                  </b-row>
                </b-col>

                <b-col
                  cols="auto"
                  class="timeline-registry left-bordered d-flex align-items-center"
                >
                  <VtBreadCrumbs
                    v-if="showBreadCrumbs"
                    :lab-id="labId"
                    :case-study="caseData"
                    :samples="samples"
                  />
                </b-col>
              </b-row>
              <b-row
                v-if="patientInformation"
                slot="footer"
              >
                <b-col
                  cols="auto"
                  class="pl-2 d-flex align-items-center"
                >
                  <vt-loading-button
                    v-if="$_canDeleteCase(casId, caseData && caseData.status.code)"
                    qa="case-index-delete-button"
                    class="btn__link btn__link--danger btn-sm mx-2"
                    :params="{ promise: showDeleteCaseModal, shortkeys: ['alt', 'del'] }"
                  >
                    {{ $tc('global.delete') }}
                  </vt-loading-button>
                  <vt-loading-button
                    v-if="$_canRegisterCase(casId, caseData && caseData.status.code)"
                    qa="case-index-finalize-button"
                    class="btn__link btn-sm mx-2"
                    :params="{ promise: registerCase, shortkeys: ['alt', 'f'] }"
                  >
                    {{ $tc('global.finalize') }}
                  </vt-loading-button>
                </b-col>
                <b-col class="d-flex justify-content-end pr-2">
                  <div
                    v-if="$refs.caseForm"
                    class="d-flex align-items-center"
                  >
                    <div
                      v-if="!isReadOnly"
                      class="switch-btn p-0 d-flex align-items-center mr-4"
                    >
                      <span class="mr-2 printed-label-switch-title">{{ $tc('registry.btn.consultation') }}</span>
                      <input
                        id="toggle-consultation"
                        v-model="$refs.caseForm.formData.caseOfReference"
                        type="checkbox"
                        class="offscreen"
                        :value="true"
                        :unchecked-value="false"
                        :disabled="disabledCaseOfReferenceButtonSwitch"
                        aria-hidden="true"
                      >
                      <label
                        :qa="`case-index-consultation-switch-${$refs.caseForm.formData.caseOfReference}-label`"
                        for="toggle-consultation"
                        class="switch mb-0"
                      />
                    </div>
                  </div>
                  <vt-loading-button
                    v-if="$_canSaveCase(casId) || $_canCreateSample() || $_canEditSample()"
                    qa="case-index-save-button"
                    class="btn__outline btn-sm"
                    :disabled="disabledSaveButton"
                    :params="{ promise: save, shortkeys: ['alt', 's'] }"
                  >
                    <i
                      class="far fa-save mr-2"
                      aria-hidden="true"
                    />{{ $tc('global.save') }}
                  </vt-loading-button>
                  <vt-loading-button
                    v-if="$_canRegisterAndPrintCaseLabels(casId, caseData && caseData.status.code)"
                    qa="case-index-formalize-and-print-button"
                    class="btn--primary btn-sm ml-3"
                    :params="{ promise: registerCaseAndPrintLabels, shortkeys: ['ctrl', 'enter'] }"
                  >
                    <i
                      class="far fa-sticky-note mr-2"
                      aria-hidden="true"
                    />{{ $tc('global.formalizePrint') }}
                  </vt-loading-button>
                </b-col>
              </b-row>
            </b-card>
          </b-col>
        </b-row>
      </b-container>
    </div>

    <!--Modals -->
    <modal-wrapper ref="searchPatientModal">
      <search-patient-modal
        :can-select-patient="true"
        :action-button="actionPatientModal"
        :can-create-cases="true"
        id-modal="searchPatientModal"
        :select-button="selectButtonPatientModal"
        @closePreviousCasesWindow="closePreviousCasesWindow"
        @selected="onSelectedPatient($event)"
        @selectedNewCase="createNewCase($event)"
        @patientEdited="onEditPatient($event)"
      />
    </modal-wrapper>
    <modal-wrapper ref="incidentFormModal">
      <incident-form-modal
        id="incidentFormModal"
        :core-entity="coreEntity"
        @incident-added="reloadSamples"
      />
    </modal-wrapper>
    <modal-wrapper ref="incidentModalTreeWrapper">
      <common-incident-modal-tree
        v-if="patientInformation"
        @incident-added="reloadSamples"
      />
    </modal-wrapper>
    <common-attachment-tree-modal
      v-if="patientInformation"
      ref="attachmentTreeModal"
      :patient="patientInformation"
      :laboratory-id="labId"
      @attachments-updated="loadCase(casId)"
    />
    <modal-wrapper ref="modalAssociateCaseWrapper">
      <modal-associate-case
        v-if="casId && patientPreviousCases"
        :case-id="casId"
        :associated-cases="associatedCases"
        :patient="patientInformation"
        :case-number="caseNumber"
        :lab-id="labId"
        :cases-by-owner-laboratory="casesByOwnerLaboratory"
        @associatedCasesUpdated="getPatientCases()"
      />
    </modal-wrapper>

    <patient-fusion-modal-component
      ref="patientFusionModal"
      :cas-pat-id="patientInformation && patientInformation.patId"
      @updateCase="loadCase(casId)"
    />

    <modal-wrapper ref="editPatientModal">
      <edit-patient-modal
        v-if="patientInformation"
        @edited="onEditPatient($event)"
      />
    </modal-wrapper>
    <modal-wrapper ref="modalPatientCard">
      <modal-patient-card
        v-if="patientInformation"
        id="modalPatientCard"
        :patient-id="patientInformation.patId"
      />
    </modal-wrapper>
    <modal-wrapper ref="addFileModalWrapper">
      <add-file-modal
        id="addFileModal"
        ref="addFileModal"
        :attachment-entity-type="ATTACHMENT_ENTITY_TYPE"
        :entities-id-array="entitiesIdArray"
        @attachmentsAdded="onAttachmentsAdded"
      />
    </modal-wrapper>
    <print-component ref="printComponent" />
    <modal-wrapper ref="modalCustomPrint">
      <modal-custom-print @handleCustomPrint="handleCustomPrint($event)" />
    </modal-wrapper>
    <modal-pathologist-assignment ref="pathologistAssignmentModal" />
    <modal-wrapper ref="createCaseModal">
      <create-case-modal
        id="createCaseModal"
        :can-create-cases="true"
        continue-with-case-creation
        id-modal="searchPatientModal"
        @selectedPatientFromHis="onSelectedPatientFromHis"
        @selected="onSelectedPatient($event)"
      />
    </modal-wrapper>
    <modal-unsaved-changes
      id="caseIndexUnsavedChangesModal"
      ref="unsavedChangesModal"
    />
    <modal-wrapper ref="patientSelectionModalWrapper">
      <patient-selection-modal
        id="patientSelectionModal"
        continue-with-case-creation
        @updateLocalPatientAndContinueWithCaseCreation="onUpdateLocalPatientAndContinueWithCaseCreation"
        @continue="onPatientSelectionModalContinue"
      />
    </modal-wrapper>
    <modal-wrapper ref="modalRepeatedPatients">
      <modal-repeated-patients
        :reason-for-showing-modal="$tc('component.patientFusion.selectToCreateCase')"
        @patientSelectedFromHis="onSelectedPatientFromHis"
        @patientSelected="createNewCase"
      />
    </modal-wrapper>
    <modal-wrapper ref="reportsModalFormWrapper">
      <reports-modal-form />
    </modal-wrapper>
    <valuable-concepts-modal
      ref="valuableConceptsModal"
      @refresh="onRefreshCaseView()"
    />
    <activity-concepts-modal ref="activityConceptsModal" />
  </div>
</template>

<script>
import CaseServices from '@/modules/RegistryModule/services/registryCaseServices'
import commonCaseServices from '@/api/resources/common/commonCaseServices'
import EditPatientModal from '@/components/patient/EditPatientModal'
import SearchPatientModal from '@/components/searcherPatientModal/SearchPatientModal'
import ModalAssociateCase from '@/modules/RegistryModule/views/editCase/modals/ModalAssociateCase'
import PatientFusionModalComponent
    from '@/modules/RegistryModule/views/editCase/modals/patient-fusion/modal/patient-fusion-modal.vue'
import CommonAttachmentTreeModal from '@/components/attachments/CommonAttachmentModalTree'
import CommonIncidentModalTree from '@/components/incidentsModal/CommonIncidentModalTree'
import PatientPreviousCases from '@/modules/RegistryModule/views/editCase/PatientPreviousCases'
import Encoder from '@/utils/encoder'
import CaseForm from './CaseForm'
import CaseSamples from './CaseSamples'
import CaseStatusBadge from '@/components/common/CommonCaseStatusBadge'
import VtBreadCrumbs from '@/components/common/VtBreadCrumbs'
import ModalPatientCard from '@/components/modals/ModalPatientCard'
import VtBubble from '@/components/common/VtBubble'
import AddFileModal from '@/components/AddFileModal'
import IncidentFormModal from '@/components/incidentsModal/IncidentFormModal'
import { getCaseDeletionConfirmationMessage } from '@/api/resources/ConfirmationMessageResource'
import { CASE_STATES } from '@/api/resources/entityState/case-state'
import ModalCustomPrint from '@/components/modals/ModalCustomPrint'
import ModalPathologistAssignment from '@/modules/RegistryModule/views/editCase/modals/ModalPathologistAssignment'
import StudyRequestDetail from '@/components/common/detail/CommonRequestDetail'
import sampleServices from '@/modules/RegistryModule/services/sampleServices'
import studySidebarStore from '@/modules/RegistryModule/sideBar/case/registryCaseSideBarStore'
import { IdentifierTypeScope } from '@/api/resources/common/initialization/common-initialization.dto'
import { PATIENT_IDENTIFIER } from '@/api/resources/identifierScopesResource'
import CustomConfigResource from '@/api/resources/ui/CustomConfigResource'
import CreateCaseModal from '@/modules/RegistryModule/components/CreateCaseModal'
import commonPatientServices from '@/api/resources/common/commonPatientServices'
import { ATTACHMENT_ENTITY_TYPE } from '@/api/resources/attachment/attachmentResource'
import SearcherModuleEntitySelection from '@/components/SearcherModuleEntitySelection'
import PatientResource from '@/api/resources/PatientResource'
import { CaseOperation } from '@/api/operations/models/case-operation'
import ModalUnsavedChanges from '@/components/modals/ModalUnsavedChanges'
import { CaseOperationGuardMixin } from '@/api/operations/mixins/guards/case-operation-guard.mixin'
import { PatientOperationGuardMixin } from '@/api/operations/mixins/guards/patient-operation-guard.mixin'
import { IncidentOperationGuardMixin } from '@/api/operations/mixins/guards/incident-operation-guard.mixin'
import { SampleOperationGuardMixin } from '@/api/operations/mixins/guards/sample-operation-guard.mixin'
import personnelAutomaticAssignmentService from '@/modules/RegistryModule/services/personnelAutomaticAssignmentService'
import commonRequestService from '@/api/resources/common/commonRequestService'
import { cloneDeep } from 'lodash'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import patientDiscrepancyServices from '@/api/resources/patientDiscrepancyServices'
import PatientSelectionModal from '@/modules/RegistryModule/components/patientDiscrepancyModal/PatientSelectionModal'
import ModalRepeatedPatients from '@/modules/RegistryModule/views/editCase/modals/ModalRepeatedPatients'
import ReportsModalForm from '@/modules/RegistryModule/views/editCase/modals/reports-modal-form/reports-modal-form.vue'
import ValuableConceptsModal from '@/components/valuable-concepts-modal/valuable-concepts-modal.vue'
import ActivityConceptsModal from '@/components/activity-concepts-modal/activity-concepts-modal.vue'
import AlertBanner from '@/components/common/AlertBanner/alert-banner.vue'
import { viewerService } from '@/modules/viewerModule/services/viewer.service'
import { UiComponentUtils } from '@/shared/util/ui-component-utils'
import { ModuleOperationGuard } from '@/api/operations/guards/module-operation-guard'
import { router } from '@/router'
import { HelperUtils } from '@/shared/util/helper-utils'
import { reportType } from '@/modules/printModule/print-component.model'
import { PromiseUtils } from '@/shared/util/promise-utils'
import { store } from '@/store/store'
import { BillingOperation } from '@/api/operations/models/billing-operation'

const ROUTE_NEW_CASE = 'newCase'
const ROUTE_EDIT_CASE = 'registryEditCase'
const ROUTE_CASE_GRID = 'caseGrid'

const CASE_INDEX_TAB = {
    CASE_TAB: 0,
    SAMPLE_TAB: 1
}

export default {
    name: 'CaseIndex',
    components: {
        SearchPatientModal,
        EditPatientModal,
        PatientFusionModalComponent,
        CommonAttachmentTreeModal,
        CommonIncidentModalTree,
        PatientPreviousCases,
        ModalAssociateCase,
        CaseForm,
        CaseSamples,
        VtBreadCrumbs,
        ModalPatientCard,
        VtBubble,
        CaseStatusBadge,
        AddFileModal,
        IncidentFormModal,
        ModalCustomPrint,
        ModalPathologistAssignment,
        StudyRequestDetail,
        CreateCaseModal,
        SearcherModuleEntitySelection,
        ModalUnsavedChanges,
        PatientSelectionModal,
        ModalRepeatedPatients,
        ReportsModalForm,
        ValuableConceptsModal,
        ActivityConceptsModal,
        AlertBanner
    },
    mixins: [
        SampleOperationGuardMixin,
        CaseOperationGuardMixin,
        PatientOperationGuardMixin,
        IncidentOperationGuardMixin
    ],
    beforeRouteLeave(to, from, next) {
        this.checkBeforeRoute(next)
    },
    beforeRouteUpdate(to, from, next) {
        this.checkBeforeRoute(next)
    },
    data() {
        return {
            caseOperation: CaseOperation,
            patientPreviousCasesWindowsReference: null,
            associatedCases: [],
            printedLabel: false,
            selectButtonPatientModal: null,
            tabIndex: CASE_INDEX_TAB.CASE_TAB,
            CASE_STATES: CASE_STATES,
            newCaseData: null,
            reloadNewCase: false,
            isReassign: false,
            encoder: new Encoder(),
            caseData: null,
            selected: null,
            caseNumber: null,
            showPreviousCases: false,
            patientInformation: null,
            patientPreviousCases: null,
            actionPatientModal: null,
            coreEntity: '',
            casePathologistAutomaticAssignmentActive: false,
            studySidebarStore: studySidebarStore,
            toggleSidepanel: false,
            caseTypeId: null,
            uicomponentExternalLabel: 'uicomponent.registry.caseFormExternalLabel',
            uiComponentRegistryCaseWithSamplesOnSlide: 'uicomponent.registry.casewithsamplesonslide.modalconfig',
            uiComponentLabelsFormatMayHaveChanged: 'uicomponent.registry.labelsFormatMayHaveChanged.modalconfig',
            casesByOwnerLaboratory: [],
            samples: [],
            patientPreviousCasesCountByLaboratory: 0
        }
    },
    computed: {
        routePath() {
            return this.$route.path
        },
        routeParamsId() {
            return this.$route.params.id
        },
        showBreadCrumbs() {
            return this.caseData && !this.$_isCaseDraft(this.caseData.status.code)
        },
        disabledSaveButton() {
            return this.$_isCaseValidated(this.caseStatus?.code)
        },
        disabledPrintedLabelButtonSwitch() {
            return this.casId ? !this.$_isCaseDraft(this.caseStatus && this.caseStatus.code) : false
        },
        disabledCaseOfReferenceButtonSwitch() {
            return this.casId ? this.$_isCaseValidated(this.caseStatus?.code) : false
        },
        visibleReadOnlyCaseNumber() {
            const isCaseDraftOrNewCase = this.$_isCaseDraft(this.caseData?.status.code) || !this.casId
            return isCaseDraftOrNewCase ? !this.printedLabel : !this.$_isCaseDraft(this.caseData?.status.code)
        },
        isReadOnly() {
            return !this.$_canSaveCase(this.casId)
        },
        canViewReport() {
            return this.caseNumber && !this.$_isCaseDraft(this.caseData?.status.code)
        },
        entitySelectorOptionsForSearch() {
            if (this.hasLabOperationGlobalMixin(this.labId, CaseOperation.CREATE)) {
                return [
                    { scopes: [IdentifierTypeScope.CENTER_PATIENT, IdentifierTypeScope.PERSON], icon: 'fal fa-user-circle', text: this.$tc('component.searcher.patient'), id: PATIENT_IDENTIFIER },
                    { scopes: [IdentifierTypeScope.CASE, IdentifierTypeScope.SAMPLE], icon: 'fal fa-chart-network', text: this.$tc('registry.searcher.caseOrSamples'), errorMessage: this.$tc('registry.errors.validIdentifierCase') }
                ]
            }
            return [{ scopes: [IdentifierTypeScope.CASE, IdentifierTypeScope.SAMPLE], icon: 'fal fa-chart-network', text: this.$tc('registry.searcher.caseOrSamples'), errorMessage: this.$tc('registry.errors.validIdentifierCase') }]
        },
        ATTACHMENT_ENTITY_TYPE() {
            return ATTACHMENT_ENTITY_TYPE.PATIENT
        },
        entitiesIdArray() {
            return this.caseData?.patient
                ? [this.caseData.patient.patId]
                : []
        },
        caseRegex() {
            return store.state.regex.filter((regex) => regex.identifierType.idtScope === IdentifierTypeScope.CASE)
        },
        patientTimeOld() {
            return this.patientInformation
                ? this.getPatientTimeOld(this.patientInformation.patBirthdate)
                : ''
        },
        defaultSamId() {
            return this.samples.length
                ? this.samples[0].samId
                : null
        },
        caseIncidentsCount() {
            return this.caseData?.incidentsCount ?? 0
        },
        caseAttachmentsCount() {
            return this.caseData?.attachmentsCount ?? 0
        },
        patientIncidentsCount() {
            return (this.caseData?.patient?.incidentsCount || this.patientInformation?.incidentsCount) ?? 0
        },
        patientAttachmentsCount() {
            return (this.caseData?.patient?.attachmentsCount || this.patientInformation?.attachmentsCount) ?? 0
        },
        casId() {
            return this.caseData?.casId
        },
        samplesCount() {
            return this.caseData?.samplesCount ?? 0
        },
        caseStatus() {
            return this.caseData?.status
        },
        requestLabel() {
            return this.caseData?.requestLabel
        },
        regex() {
            return store.state.regex
        },
        labId() {
            return store.state.labId
        },
        disabledHtmlAssociatedCasesButton() {
            return !this.casId || !this.patientPreviousCases
        },
        visibleConceptModal() {
            const isCaseDraft = this.$_isCaseDraft(this.caseStatus?.code)
            return ModuleOperationGuard.canAccessBilling && !isCaseDraft && this.caseNumber &&
                HelperUtils.hasOperationInCurrentLaboratory(BillingOperation.BILLABLE_CONCEPTS_AND_CASES_ACCESS)
        },
        hasUnauthorizedBillableConcepts() {
            return this.caseData?.hasUnauthorizedBillableConcepts
        },
        visibleHasUnauthorizedBillableConceptsIcon() {
            return !!(ModuleOperationGuard.canAccessBilling && this.hasUnauthorizedBillableConcepts && this.caseNumber)
        },
        disableReassingPatient() {
            return !this.caseData || !this.$_canBePatientReassignedToCase(this.caseData)
        }
    },
    watch: {
        async routePath() {
            await this._load()
        }
    },
    created() {
        this.$eventHub.$on('changeRouteWithCheckChanges', this.checkIfDataHasBeenChangedWhenChangedLaboratoryAndGoToRoute)
    },
    async mounted() {
        await this._load()
    },
    beforeDestroy() {
        this.$eventHub.$off('changeRouteWithCheckChanges', this.checkIfDataHasBeenChangedWhenChangedLaboratoryAndGoToRoute)
        this.$eventHub.$off('updatePatientInformation', this.onUpdatePatientInformation)
    },
    methods: {
        checkBeforeRoute(goToRoute) {
            if (!router.currentRoute.meta.skipChecking) {
                this.checkIfDataHasBeenChangedAndGoToRoute(goToRoute)
            } else {
                router.currentRoute.meta.skipChecking = false
                goToRoute()
            }
        },
        async onPatientSelectionModalContinue(patient) {
            if (this.isReassign) {
                const response = await this.$_toCallUnique(CaseServices.reassignPatient(this.labId, this.casId, patient.patId), true)
                if (response) {
                    this.$_showToastSuccess(this.$tc('registry.index.successReassignedPatient'))
                    await this.loadCase(this.casId)
                }
            } else {
                await this.createNewCase(patient)
            }
        },
        async _load() {
            if (this.$route.params.samId) {
                this.tabIndex = CASE_INDEX_TAB.SAMPLE_TAB
            }

            this.updateLoading(true)
            try {
                await this.loadCasePathologistAutomaticAssignmentActive()
                if (this.$route.params.id) {
                    try {
                        await this.loadCase(this.$route.params.id)
                        await this.getPatientCases()
                    } catch (err) {
                        await router.push({ name: 'page404' })
                    }
                } else if (this.$route.params.patId) {
                    const patientInfo = await this._getPatientById(this.$route.params.patId)
                    this._setPatientInformation(patientInfo)
                    await this.getPatientCases()
                } else if (this.$route.params.patient) {
                    this.patientInformation = this.$route.params.patient
                    await this.getPatientCases()
                } else {
                    this.reloadNewCase = true
                    await router.push({ name: ROUTE_CASE_GRID })
                }

                this.showPreviousCases = store.getters['registry/showPreviousCases']
                if (!this.casId) {
                    await this.loadSwitchExternalLabelState()
                }

                this.$eventHub.$on('updatePatientInformation', this.onUpdatePatientInformation)
            } finally {
                this.updateLoading(false)
            }
        },
        onUpdatePatientInformation(updatedPatient) {
            if (updatedPatient.patId === this.patientInformation.patId) {
                this.patientInformation = cloneDeep(updatedPatient)
            }
        },
        onLoadedSamples(samples) {
            this.samples = samples
        },
        openBatchReportsModal() {
            this.$refs.reportsModalFormWrapper.show()
        },
        showValuableConceptsModal() {
            if (this.casId) {
                this.$refs.valuableConceptsModal.show({ isBillingModule: false, caseId: this.casId, caseState: this.caseData.status.code })
            }
        },
        showActivityConceptsModal() {
            this.$refs.activityConceptsModal.show()
        },
        mapFieldsToRequest(integrationPatient) {
            return {
                address: {
                    address: integrationPatient.address?.address,
                    postalCode: integrationPatient.address?.postalCode,
                    municipalityCode: integrationPatient.address?.municipality?.code ?? null,
                    provinceCode: integrationPatient.address?.province?.code ?? null,
                    countryCode: integrationPatient.address?.country?.code ?? null
                },
                birthDate: integrationPatient.patBirthdate,
                contactInformation: {
                    email: integrationPatient.contactInformation?.email,
                    mainPhone: integrationPatient.contactInformation?.mainPhone,
                    otherPhone1: integrationPatient.contactInformation?.otherPhone1,
                    otherPhone2: integrationPatient.contactInformation?.otherPhone2
                },
                gender: integrationPatient.genderCode,
                identifiers: this.mapPersonIdentifiers(
                    integrationPatient.personIdentifiers,
                    integrationPatient.centerIdentifier,
                    integrationPatient.centerPatientIdentifierType,
                    integrationPatient.centerIdentifierCenterCode),
                name: integrationPatient.patName,
                surname1: integrationPatient.patSurname1,
                surname2: integrationPatient.patSurname2
            }
        },
        mapPersonIdentifiers(personIdentifiers, centerIdentifier, centerPatientIdentifierType, centerIdentifierCenterCode) {
            const mappedIdentifiers = []
            Object.keys(personIdentifiers).forEach(identifier => {
                if (personIdentifiers[identifier].identifier) {
                    mappedIdentifiers.push({
                        identifier: personIdentifiers[identifier].identifier,
                        identifierType: identifier
                    })
                }
            })

            const mappedIdentifier =
                mappedIdentifiers.find(identifier =>
                    identifier.identifier === centerIdentifier &&
                    identifier.identifierType === centerPatientIdentifierType)
            if (!mappedIdentifier && centerIdentifier) {
                mappedIdentifiers.push({
                    centerCode: centerIdentifierCenterCode,
                    identifier: centerIdentifier,
                    identifierType: centerPatientIdentifierType
                })
            }
            return mappedIdentifiers
        },
        async getPatientFromHis(hisPatient) {
            const integrationPatientRequest = this.mapFieldsToRequest(hisPatient)
            this.updateLoading(true)
            const [error, response] = await this.toCall(patientDiscrepancyServices.hisPatientWithDiscrepancy(this.labId, integrationPatientRequest))
            this.updateLoading(false)
            if (error) {
                this.showErrorModalGlobalFromErrorResponse(error)
                return false
            }
            return response
        },
        async patientWithOrWithoutDiscrepancy(hisPatient) {
            const integrationPatientRequest = this.mapFieldsToRequest(hisPatient)
            this.updateLoading(true)
            const [error, response] = await this.toCall(patientDiscrepancyServices.patientWithOrWithoutDiscrepancy(this.labId, integrationPatientRequest))
            this.updateLoading(false)
            if (error) {
                this.showErrorModalGlobalFromErrorResponse(error)
                return false
            }
            return response
        },
        async onSelectedPatientFromHis(patient) {
            this.$refs.createCaseModal?.hide()
            const newLocalPatient = await this.patientWithOrWithoutDiscrepancy(patient)
            // patient returned from the above endpoint is the new localPatient
            if (newLocalPatient.withDiscrepancy) {
                const patientFromHis = await this.getPatientFromHis(patient)
                // if the new local patient have discrepancies show patient selection modal to resolve
                this.$refs.patientSelectionModalWrapper.show([newLocalPatient, patientFromHis, null, null, null, this.labId])
            } else if (this.isReassign) {
                const response = await this.$_toCallUnique(CaseServices.reassignPatient(this.labId, this.casId, newLocalPatient.patId), true)
                if (response) {
                    this.$_showToastSuccess(this.$tc('registry.index.successReassignedPatient'))
                    await this.loadCase(this.casId)
                }
            } else {
                await this.createNewCase(newLocalPatient)
            }
        },
        async handleCustomPrint(customPrintData) {
            if (customPrintData.caseQuantityToPrint) {
                if (this.$refs.caseForm.hasCaseBeenChanged()) {
                    this.showErrorModalGlobal(this.$tc('print.modals.unsavedChanges'))
                } else {
                    await this.printCaseLabel(customPrintData.caseQuantityToPrint)
                    await this.loadCase(this.casId)
                }
            }
            if (customPrintData.samplesToPrint && customPrintData.samplesToPrint.length) {
                if (this.$refs.caseSamples.hasSampleBeenChanged()) {
                    this.showErrorModalGlobal(this.$tc('print.modals.unsavedChanges'))
                } else {
                    await this.printSampleLabels(customPrintData.samplesToPrint, false)
                }
            }
        },
        closePreviousCasesWindow() {
            if (this.patientPreviousCasesWindowsReference) {
                this.patientPreviousCasesWindowsReference.close()
            }
        },
        openPatientPreviousCases() {
            this.closePreviousCasesWindow()
            const routeData = router.resolve({ name: 'patientCases', params: { patId: this.patientInformation.patId, labId: this.labId } })
            this.patientPreviousCasesWindowsReference = window.open(routeData.href, '_blank')
        },
        checkIfDataHasBeenChangedAndGoToRoute(goToRoute) {
            this.closePreviousCasesWindow()
            if (this.reloadNewCase) {
                goToRoute()
            }
            if (this.$refs?.caseForm?.hasCaseBeenChanged() ||
                (this.$refs?.caseSamples?.hasSampleBeenChanged() && !this.$refs?.caseForm?.isNewSave)) {
                this.$refs?.unsavedChangesModal.askAndExecuteCallbacks(
                    () => this.save(),
                    () => goToRoute())
            } else {
                goToRoute()
            }
        },
        async checkIfDataHasBeenChangedWhenChangedLaboratoryAndGoToRoute(goToRoute) {
            router.currentRoute.meta.skipChecking = true
            this.closePreviousCasesWindow()
            if (this.reloadNewCase) {
                await HelperUtils.runActionsWhenChangeLaboratory(goToRoute)
            }
            if (this.$refs?.caseForm?.hasCaseBeenChanged() ||
                (this.$refs?.caseSamples?.hasSampleBeenChanged() && !this.$refs?.caseForm?.isNewSave)) {
                this.$refs?.unsavedChangesModal.askAndExecuteCallbacks(
                    this.save,
                    () => HelperUtils.runActionsWhenChangeLaboratory(goToRoute),
                    () => { router.currentRoute.meta.skipChecking = false }
                )
            } else {
                await HelperUtils.runActionsWhenChangeLaboratory(goToRoute)
            }
        },
        handleCaseTabsNavigation(event) {
            const indexToSelect = event.srcKey === 'left' ? this.tabIndex - 1 : this.tabIndex + 1
            if (indexToSelect >= 0 && indexToSelect < 2) {
                this.tabIndex = indexToSelect
            }
        },
        async deletePatient(patId) {
            const confirmModalResult = await this.showConfirmationModalGlobal()
            if (confirmModalResult) {
                this.updateLoading(true)
                let [err] = await this.toCall(PatientResource.delete(patId))
                this.updateLoading(false)
                if (err) {
                    let message = null
                    if (err.response && err.response.data) {
                        message = err.response.data.message
                    }
                    this.showErrorModalGlobal(message)
                    return
                }

                this.updateModalStateGlobal({
                    message: this.$tc('component.patient.searcher.patientHasBeenDeleted'),
                    icon: 'fal fa-check',
                    state: 'vt-modal vt-modal--success'
                })
                await this.loadCase(this.casId)
            }
        },
        async loadSwitchExternalLabelState() {
            const isExternalRequestLabelCustomConfig = await CustomConfigResource.get(this.uicomponentExternalLabel)
            if (isExternalRequestLabelCustomConfig && isExternalRequestLabelCustomConfig.config) {
                this.printedLabel = isExternalRequestLabelCustomConfig.config.isExternalLabel
            }
        },
        saveSwitchExternalLabelState() {
            const isExternalRequestLabel = { isExternalLabel: this.printedLabel }
            CustomConfigResource.save(this.uicomponentExternalLabel, isExternalRequestLabel)
            if (!this.printedLabel) {
                this.caseNumber = null
            }
        },
        _setPatientInformation(patientInformation) {
            this.patientInformation = patientInformation
            this.patientInformation.genderName = patientInformation.gender.name
            this.patientInformation.patBirthdate = patientInformation.birthdate
            this.patientInformation.patId = patientInformation.id
        },
        async _getPatientById(patId) {
            return this.$_toCallSecure(commonPatientServices.getPatientInformation(this.labId, patId))
        },
        async onUpdateLocalPatientAndContinueWithCaseCreation(patient) {
            if (this.isReassign) {
                const response = await this.$_toCallUnique(CaseServices.reassignPatient(this.labId, this.casId, patient.patId), true)
                if (response) {
                    this.$_showToastSuccess(this.$tc('registry.index.successReassignedPatient'))
                    await this.loadCase(this.casId)
                }
            } else {
                await this.createNewCase(patient)
            }
        },
        loadSampleTypes(caseTypeId) {
            this.caseTypeId = caseTypeId
            this.$refs.caseSamples.loadSampleTypesOnChangeCaseType(caseTypeId)
        },
        async _getCasesByOwnerLaboratory() {
            const response =
                await this.$_toCallUnique(
                    commonCaseServices.getCasesByOwnerLaboratory(this.labId, this.patientInformation.patId))
            if (response) {
                this.casesByOwnerLaboratory = response
            }
        },
        async openModalAssociatedCases() {
            await this._getCasesByOwnerLaboratory()
            this.$refs.modalAssociateCaseWrapper.show()
        },
        toggleRequestSidePanel() {
            this.toggleSidepanel = !this.toggleSidepanel
        },
        closeRequestSidePanel() {
            this.toggleSidepanel = false
        },
        activePreviousCases() {
            this.showPreviousCases = !this.showPreviousCases
            store.commit('registry/updateShowPreviousCases', this.showPreviousCases)
        },
        openModalPatientFusion() {
            this.$refs.patientFusionModal.show(this.patientInformation.patId)
        },
        openIncidentFormModal(coreEntity, entitiesIds) {
            this.coreEntity = coreEntity
            this.$nextTick(() => {
                this.$refs.incidentFormModal.show([null, entitiesIds])
            })
        },
        openIncidentModal(defaultOption) {
            this.$refs.incidentModalTreeWrapper.show([defaultOption, this.patientInformation.patId])
        },
        onOpenSampleIncidentModal(sample) {
            this.openIncidentModal({
                coreEntity: 'SAMPLE',
                casId: this.casId,
                samId: sample.id
            })
        },
        showPatientAttachmentTreeModal() {
            this.$refs.attachmentTreeModal.showPatient()
        },
        showCaseAttachmentTreeModal() {
            this.$refs.attachmentTreeModal.showCase(this.casId)
        },
        showPatientAttachmentModal() {
            this.$refs.addFileModalWrapper.show()
        },
        async onAttachmentsAdded() {
            await this.loadCase(this.casId)
        },
        async _loadAssociatedCase() {
            const response =
                await this.$_toCallSecure(
                    commonCaseServices.getAssociatedCases(this.labId, this.casId))
            if (response) {
                this.associatedCases = response
            }
        },
        async getPatientCases() {
            const patientPreviousCasesCountByLaboratoryResponse =
                await this.$_toCallSecure(
                    commonCaseServices.patientPreviousCasesCountByLaboratory(this.labId, this.patientInformation.patId))
            this.patientPreviousCasesCountByLaboratory = patientPreviousCasesCountByLaboratoryResponse?.count

            this.patientPreviousCases =
                await this.$_toCallSecure(
                    commonCaseServices.getPreviousCasesLinked(this.labId, this.patientInformation.patId))
            if (this.casId && this.patientPreviousCases) {
                await this._loadAssociatedCase()
            }
        },
        async checkValidation() {
            this.$refs.caseForm.showErrors = false
            this.$refs.caseSamples.showErrors = false
            const formValidation = this.$refs.caseForm.validate()
            if (!formValidation || !this.$refs.caseForm.formData.casRequestDate) {
                this.$refs.caseForm.showErrors = true
                this.updateModalStateGlobal(
                    {
                        message: this.$tc('studyRequest.general.requiredFieldsNotice'),
                        icon: 'fas fa-exclamation',
                        state: 'vt-modal vt-modal--warning'
                    }
                )
                return false
            }
            for (let sample of this.$refs.caseSamples.samples) {
                if (!sample.topographicCode || !sample.sampleType) {
                    this.$refs.caseSamples.showErrors = true
                    this.$refs.caseSamples.gridApi.refreshCells({ force: true })
                    this.updateModalStateGlobal(
                        {
                            message: this.$tc('studyRequest.general.requiredFieldsNotice'),
                            icon: 'fas fa-exclamation',
                            state: 'vt-modal vt-modal--warning'
                        }
                    )
                    return false
                }
            }

            let caseLabelRegexValidation = true
            if (this.printedLabel) {
                caseLabelRegexValidation = this.checkCaseLabelRegexValidation()
            }
            if (this.printedLabel && !caseLabelRegexValidation) {
                return false
            }
            if (!this.casId) {
                return this.$refs.caseSamples.checkCaseSamplesCompatibility()
            }
            return true
        },
        checkCaseLabelRegexValidation() {
            for (let regex of this.caseRegex) {
                const caseRegexObject = new RegExp('^(?:' + regex.regex + ')$')
                if (caseRegexObject.test(this.caseNumber)) {
                    return true
                }
            }
            this.$_showErrorModal(this.$tc('registry.errors.invalidCaseLabel'))
            return false
        },
        async checkIfCaseOfConsultationChanged(originalCaseClone) {
            const formData = this.$refs.caseForm.mapFormDataToRequest(this.$refs.caseForm.formData)
            if (originalCaseClone.caseOfReference !== formData.caseOfReference) {
                const modalConfigStatus = await this.labelsFormatMayHaveChangedModalConfigDontShowAgainStatus()
                if (!modalConfigStatus) {
                    const confirmationModalParams = {
                        question: this.$tc('registry.modal.labelsFormatMayHaveChanged')
                    }
                    const confirmationModalResponse = await this.$_showConfirmationModalCheckable(confirmationModalParams)
                    if (confirmationModalResponse) {
                        if (confirmationModalResponse.dontShowAgainCheckStatus) {
                            await this.$_toCallUnique(UiComponentUtils.saveCustomConfig(
                                this.uiComponentLabelsFormatMayHaveChanged,
                                { config: { modalCancelConfirmation: confirmationModalResponse.dontShowAgainCheckStatus } },
                                this.labId
                            ))
                        }
                        if (confirmationModalResponse.buttonAction) {
                            await this.printCaseAndSampleLabels()
                        }
                    }
                }
            }
        },
        async save(isRegistering = false) {
            const isValidated = await this.checkValidation()
            if (!isValidated) {
                return false
            }

            const hasCaseChanged = this.$refs.caseForm.hasCaseBeenChanged()
            const haveSamplesChanged = this.$refs.caseSamples.hasSampleBeenChanged()
            if (!hasCaseChanged && !haveSamplesChanged) {
                return true
            }
            const originalCaseClone = cloneDeep(this.$refs.caseForm.caseClone)
            const caseSavedOk = await this.$refs.caseForm.saveForm()
            if (caseSavedOk) {
                const samplesSavedOk = await this.$refs.caseSamples.saveSamples()

                if (!isRegistering) {
                    if (samplesSavedOk) {
                        await this.loadCase(this.casId)
                        this.$emit('updateSummary')
                        this.$_showToastSuccess(this.$tc('registry.index.saveFormSuccess'))
                    }

                    if (this.$refs.caseForm.isNewSave) {
                        await router.push({
                            name: ROUTE_EDIT_CASE,
                            params: {
                                id: this.casId,
                                samId: this.samples[0].id
                            }
                        })
                    }
                } else {
                    this.$nextTick(() => this.$refs.searcherModule?.focusInput())
                }
                const caseHasSomeSamplesPrinted = this.$refs.caseSamples.samples.some(sample => sample.printed)
                const casePrinted = this.$refs.caseForm.formData.casPrinted
                if (!this.$_isCaseDraft(this.caseData.status?.code) && !!this.casId && (caseHasSomeSamplesPrinted || casePrinted)) {
                    await this.checkIfCaseOfConsultationChanged(originalCaseClone)
                }
                return samplesSavedOk
            }
            return false
        },
        async registerCaseAndPrintLabels() {
            const registered = await this.registerCase()
            if (registered) {
                await this.printCaseAndSampleLabels()
            }
        },
        async registryCaseWithSamplesOnSlideModalConfigDontShowAgainStatus() {
            const registryModalConfigUiComponent = await this.$_toCallUnique(UiComponentUtils.getCustomConfig(this.uiComponentRegistryCaseWithSamplesOnSlide, this.labId))
            return registryModalConfigUiComponent?.config?.modalCancelConfirmation
        },
        async labelsFormatMayHaveChangedModalConfigDontShowAgainStatus() {
            const registryModalConfigUiComponent = await this.$_toCallUnique(UiComponentUtils.getCustomConfig(this.uiComponentLabelsFormatMayHaveChanged, this.labId))
            return registryModalConfigUiComponent?.config?.modalCancelConfirmation
        },
        async registryCaseWithSamplesOnSlide() {
            const modalConfigStatus = await this.registryCaseWithSamplesOnSlideModalConfigDontShowAgainStatus()
            if (!modalConfigStatus) {
                const confirmationModalParams = {
                    question: this.$tc('registry.cases.caseWithSamplesOnSlideRegistry')
                }
                const confirmationModalResponse = await this.$_showConfirmationModalCheckable(confirmationModalParams)
                if (confirmationModalResponse) {
                    if (confirmationModalResponse.dontShowAgainCheckStatus) {
                        await this.$_toCallUnique(UiComponentUtils.saveCustomConfig(
                            this.uiComponentRegistryCaseWithSamplesOnSlide,
                            { config: { modalCancelConfirmation: confirmationModalResponse.dontShowAgainCheckStatus } },
                            this.labId
                        ))
                    }
                    return confirmationModalResponse.buttonAction
                }
            } else {
                return modalConfigStatus
            }
        },
        async registerCase() {
            let registered = false
            if (!this.samples.length) {
                this.$_showErrorModal(this.$tc('registry.index.registerNoSamples'))
                return registered
            }
            if (!this.casePathologistAutomaticAssignmentActive &&
                    !this.$refs.caseForm.formData.pathologist) {
                this.$_showErrorModal(this.$tc('registry.index.registerNoPath'))
                return registered
            }

            const saveResponse = await this.save(true)
            if (saveResponse) {
                // Pending Re-Analysis
                // const caseHaveSamplesWithSampleOnSlide = this.$refs.caseSamples.samples.some(sample => sample.sampleOnSlide)
                // const continueWithCaseRegistry = caseHaveSamplesWithSampleOnSlide ? await this.registryCaseWithSamplesOnSlide() : true
                const continueWithCaseRegistry = true
                if (continueWithCaseRegistry) {
                    if (this.casePathologistAutomaticAssignmentActive) {
                        const successfulAssignment = await this.assignPathologistAutomatically()
                        if (successfulAssignment) {
                            registered = await this.$refs.caseForm.registerCase()
                        }
                    } else {
                        registered = await this.$refs.caseForm.registerCase()
                    }

                    if (registered) {
                        this.$_showToastSuccess(this.$tc('registry.index.registerFormSuccess'))
                        await this.$refs.caseSamples?.loadSamples()
                        this.$emit('updateSummary')
                    }
                }
            }

            await this.getPatientCases()
            if (this.$refs.caseForm.isNewSave) {
                history.pushState(null, '', `/#/registry/case/${this.casId}`)
            }
            this.$nextTick(() => this.$refs.searcherModule.focusInput())
            return registered
        },
        async assignPathologistAutomatically() {
            let selectedPathologist
            let automaticallySelected = false
            const [err, response] = await this.toCall(personnelAutomaticAssignmentService.getRecommendedPathologistsForCase(this.labId, this.casId))
            if (err) {
                this.showErrorModalGlobal()
                return false
            }
            const proposedPathologists = response
            const currentPathologist = this.$refs.caseForm.formData.pathologist
            const allPathologists = await this.$refs.caseForm.getPersonnelPathologist()

            if (proposedPathologists.length === 0) {
                if (!this.isEmpty(currentPathologist)) {
                    return true
                }
                selectedPathologist = await this.askUserToSelectPathologist(allPathologists)
            } else if (proposedPathologists.length > 1) {
                if (!this.isEmpty(currentPathologist) && proposedPathologists.find(pathologist => pathologist.id === currentPathologist.id)) {
                    return true
                }
                if (!this.isEmpty(currentPathologist)) {
                    proposedPathologists.push(this.caseData.pathologist.fullName)
                }
                selectedPathologist = await this.askUserToSelectPathologist(proposedPathologists)
            } else if (proposedPathologists.length === 1) {
                if (this.isEmpty(currentPathologist)) {
                    selectedPathologist = proposedPathologists[0]
                    automaticallySelected = true
                } else if (currentPathologist.id === proposedPathologists[0].id) {
                    return true
                } else {
                    const oldPathologistName = this.caseData.pathologist.fullName
                    const wantToChangePathologist = await this.showConfirmationModalGlobal(
                        this.$tc('registry.modal.keepOrChangeAssignedPathologist', 0, { newPathologist: proposedPathologists[0].fullName, oldPathologist: oldPathologistName }),
                        this.$tc('registry.modal.assign'), this.$tc('registry.modal.keep'))
                    if (!wantToChangePathologist) {
                        return true
                    }
                    selectedPathologist = proposedPathologists[0]
                }
            }
            if (!selectedPathologist) {
                return false
            }
            this.$refs.caseForm.changePathologist(selectedPathologist, automaticallySelected)
            return this.save(true)
        },
        async askUserToSelectPathologist(pathologistList) {
            const filteredPathologistList = pathologistList.filter(pathologist => pathologist)
            return new Promise((resolve) => {
                this.$refs.pathologistAssignmentModal.showAndSelectOne(
                    filteredPathologistList,
                    async (selectedPathologist) => {
                        resolve(selectedPathologist)
                    },
                    async () => {
                        resolve(false)
                    })
            })
        },

        /* Search methods */
        async onSampleSearch(searchInformation) {
            const response =
                await this.$_toCallUnique(
                    sampleServices.searchSampleByLabel(this.labId, searchInformation),
                    error => this.$_showErrorModal(
                        error.response.data.code === this.$apiExceptions.ENTITY_NOT_FOUND
                            ? this.$tc('component.searcher.error.noSample')
                            : undefined))

            if (response?.length) {
                let entitySelected = response[0]
                if (response.length > 1) {
                    const foundEntities =
                        response.map(entity => ({
                            entityType: IdentifierTypeScope.SAMPLE,
                            entityValue: entity,
                            laboratoryId: entity.caseStudy.laboratory.labId
                        }))
                    entitySelected = await this.$_showMultiEntitySelection(searchInformation, foundEntities)
                }

                const samples = this.$refs.caseSamples?.samples
                if (samples?.length) {
                    const foundSample = samples.find(sample => sample.id === entitySelected.samId)
                    if (foundSample) {
                        this.$nextTick(() => {
                            this.tabIndex = CASE_INDEX_TAB.SAMPLE_TAB
                            this.$refs.caseSamples.selectSample(foundSample.id)
                        })
                        return
                    }
                }

                const navigationConfirmed =
                    await this.$_confirmNavigationBecauseOfLaboratoryChange(entitySelected.caseStudy.laboratory.labId)
                if (navigationConfirmed) {
                    await router.push({
                        name: ROUTE_EDIT_CASE,
                        params: {
                            id: entitySelected.caseStudy.casId,
                            samId: entitySelected.samId
                        }
                    })
                }
            }
        },
        async onStudySearch(searchInformation) {
            const response =
                await this.$_toCallUnique(
                    CaseServices.findCaseByLabel(this.labId, searchInformation),
                    error => {
                        if (error.response.data.code === this.$apiExceptions.ENTITY_NOT_FOUND) {
                            this.$_showErrorModal(this.$tc('component.searcher.error.noCase'))
                        } else {
                            this.$_showErrorModalFromErrorResponse(error)
                        }
                    })

            if (response?.length) {
                let entitySelected = response[0]
                if (response.length > 1) {
                    const foundEntities =
                        response.map(entity => ({
                            entityType: IdentifierTypeScope.CASE,
                            entityValue: entity,
                            laboratoryId: entity.laboratory.labId
                        }))
                    entitySelected = await this.$_showMultiEntitySelection(searchInformation, foundEntities)
                }

                if (entitySelected.casId === this.caseData?.casId) {
                    this.$_showWarningModal(this.$tc('registry.cases.alreadyLoaded'))
                    return
                }

                const navigationConfirmed =
                    await this.$_confirmNavigationBecauseOfLaboratoryChange(entitySelected.laboratory.labId)
                if (navigationConfirmed) {
                    await router.push({
                        name: ROUTE_EDIT_CASE,
                        params: {
                            id: entitySelected.casId
                        }
                    })
                }
            }
        },
        async onPatientSearch(searchInformation) {
            const params = {
                size: PAGINATOR_BIG_PAGE_SIZE,
                id: searchInformation.identifierSearch,
                surname1: searchInformation.patientSurname1Search,
                surname2: searchInformation.patientSurmname2Search,
                middleName: searchInformation.patientMiddlenameSearch,
                requestingLaboratoryId: this.labId,
                name: searchInformation.patientNameSearch,
                centerCode: searchInformation.center,
                identifierType: searchInformation.type
            }
            const response =
                await this.$_toCallUnique(
                    PatientResource.paginatorMultiSourced(params),
                    error => this.$_showErrorModal(
                        error.response.data.code === this.$apiExceptions.RESULT_LIMIT_EXCEEDED
                            ? this.$tc('component.patient.searcher.tooManySearchResults')
                            : undefined))

            const patients = response?.content
            if (!patients?.length) {
                this.$_showErrorModal(this.$tc('component.patient.searcher.noResults'))
            } else if (patients.length > 1) {
                this.$refs.modalRepeatedPatients.show([patients, searchInformation.identifierSearch])
            } else {
                if (!patients[0].patId) {
                    await this.onSelectedPatientFromHis(patients[0])
                } else {
                    const patientInfo = await this._getPatientById(patients[0].patId)
                    if (patientInfo) {
                        await this.createNewCase(patientInfo)
                    }
                }
            }
        },
        // TODO Waiting for clear functionality on request search in case grid
        /* async requestSearch(searchInformation) {
          this.updateLoading(true)
          let filterId = {
            reqExternalId: {
              filterType: 'text',
              type: 'equals',
              filter: searchInformation.search
            }
          }
          let filterModel = {
            filterModel: this.encoder.encode(this.encoder.parseBooleans(filterId))
          }
          const request = await registryRequestResource.paginator(this.labId,
            filterModel)
          if (request.content.length) {
            let requestContent = request.content[0]
            if (requestContent.caseStudy) {
              await router.push({ name: ROUTE_EDIT_CASE, params: { id: requestContent.caseStudy.casId } })
            } else {
              this.updateModalStateGlobal({
                message: this.$tc('component.searcher.noCaseAssociated'),
                icon: 'fas fa-info',
                state: 'vt-modal vt-modal--info'
              })
              this.updateLoading(false)
            }
          } else {
            this.updateModalStateGlobal({
              message: this.$tc('component.searcher.noRequest'),
              icon: 'fas fa-info',
              state: 'vt-modal vt-modal--info'
            })
            this.updateLoading(false)
          }
        }, */

        async deleteCase() {
            let isAutomaticallyGenerated = false
            this.updateLoading(true)
            if (this.caseData.requestLabel) {
                isAutomaticallyGenerated = await this.$_toCallUnique(
                    commonRequestService.isAutomaticallyGenerated(this.labId, this.caseData.requestLabel)
                )
            }
            let [err] = await this.toCall(
                CaseServices.deleteCase(this.labId, this.casId)
            )
            if (err) {
                if (err.response.data.code === this.$apiExceptions.ASSOCIATED_SAMPLES_FOUND) {
                    this.updateModalStateGlobal({
                        message: this.$tc('registry.errors.existingSamplesDeleteError'),
                        icon: 'fas fa-exclamation',
                        state: 'vt-modal vt-modal--warning'
                    })
                    this.updateLoading(false)
                    return
                }
                this.updateModalStateGlobal({
                    message: this.$tc('component.modalError.title'),
                    icon: 'fal fa-times',
                    state: 'vt-modal vt-modal--error'
                })
                this.updateLoading(false)
                return
            }
            this.updateLoading(false)

            this.updateModalStateGlobal({
                message: this.$tc('registry.modal.caseDeleteSuccessful'),
                icon: 'fal fa-check',
                state: 'vt-modal vt-modal--success'
            })
            if (this.caseData.requestLabel && !isAutomaticallyGenerated) {
                this.showToastSuccessGlobal(this.$tc('registry.modal.caseDeleteRequestStateChanged', { requestLabel: this.caseData.requestLabel }))
            }
            await this.updateLastModified()
            await router.push({ name: ROUTE_CASE_GRID })
        },
        async updateLastModified(idToSelect = null) {
            let lastModified = await CaseServices.lastModifiedCases(this.labId)
            lastModified.forEach((studyCase) => { studyCase.isCase = true })
            this.updateSideBarOptionChildren(this.studySidebarStore.state.options.expanded, 'lastCases', lastModified)
            if (idToSelect) {
                const optionToSelect = lastModified.find((caseStudy) => caseStudy.id === idToSelect)
                this.studySidebarStore.setSelectedOption(optionToSelect)
            }
        },
        async loadCase(caseId) {
            const caseData =
                await this.$_toCallSecure(
                    CaseServices.getCase(this.labId, caseId))
            this.mapData(caseData)
            viewerService.closeViewersOfDifferentCase(`${this.caseData.casId}`)
        },
        async reloadSamples() {
            await this.loadCase(this.casId)
            await this.$refs.caseSamples?.loadSamples()
        },
        async loadCasePathologistAutomaticAssignmentActive() {
            const response =
                await this.$_toCallSecure(
                    personnelAutomaticAssignmentService.isCasePathologistAutomaticAssignmentActive(this.labId))
            if (response) {
                this.casePathologistAutomaticAssignmentActive = response
            }
        },
        openEditPatientModal() {
            this.$refs.editPatientModal.show([this.patientInformation.patId])
        },
        showModal() {
            this.$refs['modalPatientCard'].show()
        },
        async showDeleteCaseModal() {
            const modalResponse = await this.showConfirmationModalGlobal(getCaseDeletionConfirmationMessage(this.caseData))
            if (modalResponse) {
                await this.deleteCase()
            }
        },
        async onSelectedPatient(patientDetail) {
            if (this.isReassign) {
                this.updateLoading(true)
                let [err] = await this.toCall(CaseServices.reassignPatient(this.labId, this.casId, patientDetail.patId), true)
                this.updateLoading(false)
                if (err) {
                    return
                }

                this.updateModalStateGlobal({
                    message: this.$tc('registry.index.successReassignedPatient'),
                    icon: 'fal fa-check',
                    state: 'vt-modal vt-modal--success'
                })
                await this.loadCase(this.casId)
            } else {
                const patientInfo = await this._getPatientById(patientDetail.patId)
                if (patientInfo) {
                    await this.createNewCase(patientInfo)
                }
            }
        },
        async resetData() {
            this.caseData = null
            this.caseNumber = null
            await this.getPatientCases()
            this.$refs.caseForm.resetData()
            await this.$refs.caseSamples.resetData()
        },
        async createNewCase(patientDetail) {
            this.$root.$emit('bv::hide::popover')
            const patId = patientDetail.patId | patientDetail.id
            if (this.$route.name !== ROUTE_NEW_CASE) {
                await router.push({ name: ROUTE_NEW_CASE, params: { patId } })
                await this.resetData()
            } else {
                if (this.$refs.caseForm.hasCaseBeenChanged() || this.$refs.caseSamples.hasSampleBeenChanged()) {
                    const modalConfirmationResponse = await this.showConfirmationModalGlobal(this.$tc('global.exitWithUnsavedChanges'))
                    if (!modalConfirmationResponse) {
                        return
                    }
                }
                const patientInfo = await this._getPatientById(patId)
                this._setPatientInformation(patientInfo)
                await router.push({ name: ROUTE_NEW_CASE })
                await this.resetData()
                await this.loadSwitchExternalLabelState()
            }
        },
        openPatientModal() {
            this.$refs.searchPatientModal.show()
        },
        openCreateCaseModal(isReassign = false) {
            this.isReassign = isReassign
            this.$refs.createCaseModal.show([isReassign])
        },
        openCustomPrintModal() {
            this.$refs.modalCustomPrint.show([this.samples])
        },
        mapData(caseData) {
            this.caseData = caseData

            this.printedLabel = caseData.isExternalLabel
            this.patientInformation = caseData.patient
            this.caseNumber = caseData.casCasenumber
        },
        onEditPatient(patient) {
            this.patientInformation = patient
            this.patientInformation.genderName = patient.gender.name
        },

        /**
         * Print Methods
         */
        async printSampleLabels(samples, withUserDefaultNumberOfCopies = true) {
            const samplesToPrint = samples.filter(sample => sample.id)
            const printSuccess = await this.$refs.printComponent.printSampleLabelsFromRegistry(
                this.labId,
                this.casId,
                samplesToPrint,
                withUserDefaultNumberOfCopies
            )
            if (printSuccess) {
                await this.$refs.caseSamples.loadSamples()
            }
        },
        async onCaseFormPrintCaseLabel() {
            if (this.$refs.caseForm.hasCaseBeenChanged()) {
                this.showErrorModalGlobal(this.$tc('print.modals.unsavedChanges'))
            } else {
                await this.printCaseLabel()
                await this.loadCase(this.casId)
            }
        },
        async onCaseSamplesPrintSampleLabels(samples) {
            if (this.$refs.caseSamples.hasSampleBeenChanged()) {
                this.showErrorModalGlobal(this.$tc('print.modals.unsavedChanges'))
            } else {
                await this.printSampleLabels(samples)
            }
        },
        async printCaseLabel(numberOfCopies = 1) {
            await this.$refs.printComponent.printCaseLabel(this.labId, this.caseData, numberOfCopies)
        },
        async onPrintLabelsCaseButtonClick() {
            if (this.$refs.caseForm.hasCaseBeenChanged() || this.$refs.caseSamples.hasSampleBeenChanged()) {
                this.showErrorModalGlobal(this.$tc('print.modals.unsavedChanges'))
            } else {
                await this.printCaseAndSampleLabels()
            }
        },
        async printCaseAndSampleLabels() {
            await this.printCaseLabel()
            await this.printSampleLabels(this.samples)
            await this.$refs.caseSamples.loadSamples()
            await this.loadCase(this.casId)
        },
        async printCaseAPReport() {
            let includeAssociated = false
            if (this.associatedCases.length) {
                includeAssociated = await this.showConfirmationModalGlobal(this.$tc('global.modal.case.viewAssociatedCasesConfirmation'))
            }
            await this.$refs.printComponent.printCaseReportFromRegistry(this.labId, this.casId, includeAssociated)
        },
        async getCaseReportPDF() {
            const getCaseReportPDFParams = {
                laboratoryId: this.labId,
                caseId: this.casId,
                caseLabel: this.caseData?.casCasenumber,
                print: false
            }
            await HelperUtils.getCaseReportPDF(getCaseReportPDFParams)
        },
        async exportPatientPreviousCasesReportPDF() {
            await this.$refs.printComponent.printReport(
                reportType.CASE_REPORT,
                () => PromiseUtils.toCallSecure(
                    PatientResource.getPreviousCaseAPReport(
                        this.labId,
                        this.patientInformation.patId,
                        true
                    )
                )
            )
        },
        async onRefreshCaseView() {
            await this.loadCase(this.$route.params.id)
            await this.$refs.caseSamples.loadSamples()
        }
    }
}
</script>

<style lang="scss">
    /*-- Provisional layout for overflow responsive --*/
    /* -- TODO Refactoring required --*/
    .flex-column-grow {
        flex-grow: 1;
        display: flex;
        flex-direction: column;
        min-height: 0;
    }
    .registryForm {
        @extend .flex-column-grow !optional;
        padding-bottom: 45px;

        .mt-2.container-fluid {
            @extend .flex-column-grow !optional;

            .registry-card {
                flex-grow: 1;
                min-height: 0;

                .p-0.col {
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                    min-height: 0;

                    .card {
                        background: transparent;
                        @extend .flex-column-grow !optional;

                        .card-body {
                            @extend .flex-column-grow !optional;

                            .main-row {
                                flex-grow: 1;
                                min-height: 0;

                                .main-col {
                                    height: 100%;
                                    display: flex;
                                    flex-direction: column;
                                    min-height: 0;

                                    .case-content-section {
                                        @extend .flex-column-grow !optional;

                                        .p-0.col {
                                            @extend .flex-column-grow !optional;

                                            .samples-tabs {
                                                flex-grow: 1;
                                                min-height: 0;

                                                .tab-content {
                                                    height: 100%;
                                                    display: flex;
                                                    flex-direction: column;
                                                    min-height: 0;

                                                    .tab-pane {

                                                        .section-content {
                                                            flex-grow: 1;
                                                            min-height: 0;
                                                            overflow-y: auto;
                                                        }

                                                        .case-samples {
                                                            @extend .flex-column-grow !optional;

                                                            .samples-grid-container {
                                                                @extend .flex-column-grow !optional;

                                                                .p-0.col {
                                                                    flex-grow: 1;
                                                                    min-height: 0;

                                                                    .registry-sample-grid {
                                                                        height: 100% !important;
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
            .previous-cases-column {
                min-height: auto;
                height: 100%;
                .previous-cases-scroll-area {
                    flex-grow: 1;
                    overflow-y: auto;
                }
            }
        }
    }

    /*--GENERAL--*/
    .registryForm .bordered-column-right {
        border-right: 1px solid vt-color(general-border);
    }
    .registryForm .card {
        border: 0 !important;
    }
    .registry-card .case-header-section {
        min-height: 40px;
    }
    .registry-card .sticky-header {
        position: sticky;
        top: 0;
        z-index: 1;
        background: #FFF;
    }
    .registry-card .main-title {
        color: vt-color(primary);
        font-size: font-size(very-normal);
        margin: 0 !important;
    }
    .registryForm .main-card {
        border-color: transparent;
    }
    .registry-card .bordered-column-right {
        border-right: 1px solid vt-color(general-border);
    }
    .registry-card .tab-pane.card-body {
        padding: 0 5px;
    }
    .registry-card .tabs.row,
    .registry-card .no-tabs-area {
        border: 1px solid vt-color(general-border);
        margin: 0 10px 10px 10px;
        min-height: 400px;
    }

    .registry-card .nav-pills .nav-link {
        border-radius: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        border-bottom: 2px solid vt-color(white-light);
    }
    .registry-card .tab-bubble {
        position: relative;
    }
    .registry-card .section-content {
        overflow-y: auto;
        overflow-x: hidden;
    }
    .registry-card .previous-cases-column {
        max-width: 185px !important;
    }
    .hasErrors {
        color: vt-color(red) !important;
    }

    .registryForm .printed-label {
        border-bottom: 2px solid vt-color(primary);
    }

    .registryForm .printed-label-switch-title {
        font-size: font-size(small);
    }

    .registryForm .printed-label .input-group-prepend .input-group-text {
        border-radius: 14px;
        color: vt-color(search-bar-text);
        border: none;
        background-color: inherit;
        font-size: 0.85rem;
    }
    .registryForm .printed-label input {
        width: 135px;
        border: none;
        background-color: inherit;
        color: vt-color(primary);
        font-size: font-size(very-normal);
        font-weight: bold;
        height: 100%;
    }

    .registryForm .printed-label input::placeholder {
        color: vt-color(base-text);
        font-weight: normal;
        font-size: font-size(small);
    }

    .registryForm .printed-label i {
        font-size: 1rem;
    }

    .registryForm .switch {
        position: relative;
        display: inline-block;
        width: 40px;
        height: 20px;
        background-color: rgba(0, 0, 0, 0.25);
        border-radius: 20px;
        transition: all 0.3s;
        cursor: pointer;
    }

    .registryForm .switch::after {
        content: "";
        position: absolute;
        width: 18px;
        height: 18px;
        border-radius: 18px;
        background-color: white;
        top: 1px;
        left: 1px;
        transition: all 0.3s;
    }

    .registryForm input[type="checkbox"]:checked + .switch::after {
        transform: translateX(20px);
    }

    .registryForm input[type="checkbox"]:checked + .switch {
        background-color: vt-color(primary);
    }

    .registryForm .offscreen {
        position: absolute;
        left: -9999px;
    }
</style>

<style lang="scss" scoped>
    .footer-check{
        font-size: font-size(very-small);
    }

    .footer-check .custom-control-label span {
        color: vt-color(negative-text) !important;
    }

    .registryForm .btn-badge{
        @include badgeCounter();
    }

    .registryForm .sample-icons .btn-badge {
        position:absolute;
        top:8px;
        left: 12px;
    }

    .registryForm .entity-info {
        flex: 0 0 20% !important;
    }

    .sq-btn.btn-secondary {
        background: transparent;
        border: none;
        border-radius: 0;
        font-size: .9rem !important;
    }

    .simple-pop-inside-reg {
        list-style: none;
        padding: 10px;
        margin: 0;
    }

    .simple-pop-inside-attach {
        padding: 10px;
        margin: 0;
    }

    .simple-pop-inside-attach h3 {
        font-size: 1rem;
        font-weight: normal;
    }

    .simple-pop-inside-attach .shortcut {
        border: 1px solid rgba(0, 0, 0, 0.125);
        border-radius: 5px;
        box-shadow: 0 1px 1px 0 #9c9c9c;
    }

    .registryForm .vt-breadcrumbs {
        height: 60vh !important;
    }

    .registryForm .simple-line-bread {
        height: 20% !important;
    }

    .ag-cell-inline-editing {
        overflow: visible;
    }

    .registryForm .icon-collapse i {
        font-size: 2rem !important;
    }

    .preview-studies-ellipsis {
        position: absolute;
        right: 1rem;
    }

    .btn-pop {
        padding: 0;
        margin: 0;
        background: transparent;
        border: transparent;
        color: vt-color(primary);
        font-size: 1.3rem;
    }

    .btn-pop:hover {
        border: transparent;
        background: transparent;
        color: vt-color(dark-primary);
    }

    .pop-list {
        list-style: none;
        font-size: 0.9rem;
        padding: 4% 10%;
        color: #707070;
        cursor: pointer;
    }

    .pop-list:hover {
        background: vt-color(light-primary);
    }

    .form-label label {
        width: 100%;
        margin-bottom: 0.1rem;
    }

    .form-label button {
        float: right;
        margin-top: -0.3rem;
    }

    .ag-grid {
        height: 500px;
        background-color: #dadada;
    }

    /*--DATOS ESTUDIO--*/

    .buttongroup {
        position: absolute;
        right: 1.1rem;
        top: 1.4rem;
        z-index: 1;
    }

    .buttongroup span {
        font-size: 1rem;
    }

    .card-footer {
        background-color: #fff;
        padding: 0.25rem 1.20rem;
    }

    .form-registro label {
        font-size: 1rem;
    }

    .form-box .structure {
        border: 1px solid vt-color(light-primary);
        border-radius: 1%;
        margin-bottom: 1rem;
        border-left: 2px solid vt-color(primary);
        -webkit-box-shadow: 1px 1px 5px 0 rgba(125, 125, 125, 0.64);
        -moz-box-shadow: 1px 1px 5px 0 rgba(125, 125, 125, 0.64);
        box-shadow: 1px 1px 5px 0 rgba(125, 125, 125, 0.64);
    }

    /*--TIMELINE--*/

    .line-tl {
        width: 3px;
        background-color: vt-color(primary);
        height: 100%;
        position: absolute;
        top: 0;
        margin-left: 32%;
    }

    .linea {
        background-color: vt-color(primary);
        height: 65%;
        position: absolute;
        width: 3px;
        margin-left: 3%;
        margin-top: 50%;
    }
    .timeline-registry {
        max-width: 40px !important;
        border-left: 1px solid vt-color(card-border);
        padding: 0 0 0 5px;
    }
    .card-body {
        padding-top: 0;
        padding-bottom: 0;
    }

    /*--PATIENT REQUEST DETAIL--*/

    .detail-header {
        display: none !important;
    }

    .sr-detail-block h4 {
        color: vt-color(primary) !important;
        font-weight: normal !important;
    }

    .sr-detail-block {
        margin-bottom: 1.5rem !important;
    }

    .sr-detail-data-block {
        background-color: #fafafa !important;
        border-radius: 5px;
    }

    .collapsable-card-content {
        margin-bottom: 0;
    }

    .observations {
        margin-bottom: -1.6rem !important;
    }

    /*--Tabs--AÑADIR EN LUGAR CORRESPONDIENTE--*/

    .vt-tabs .nav-tabs .nav-link,
    .vt-tabs--small .nav-tabs .nav-link {
        padding: 0.4rem 2rem;
        font-size: 1rem;
        letter-spacing: 0.1rem;
        font-weight: bold;
    }

    .vt-tabs .nav-tabs .nav-link.active,
    .vt-tabs--small .nav-tabs .nav-link.active {
        color: vt-color(primary);
        border-bottom: 1px solid vt-color(primary);
    }

    .collapsable-card-content {
        padding-top: 1rem !important;
    }

    /*--BLOQUE ESTUDIO--*/

    .study-btns {
        margin-top: -12px;
        font-size: 1.3rem;
    }

    /*--BLOQUE PREVIAS--*/

    .new-case-btn {
        background-color: transparent;
        border-color: vt-color(primary);
        color: vt-color(primary);
    }

    .new-case-btn:hover {
        background-color: vt-color(primary);
        border-color: vt-color(primary);
        color: #fff;
    }

    .btn--primary {
        background-color: vt-color(primary);
        border-color: vt-color(primary);
        color: #fff;
    }

    .card-header {
        background: #f7f7f7;
    }

    .previous-active-btn:hover {
        background-color: rgba(0, 0, 0, 0.4);
        border-color: transparent;
        color: #fff;
    }

    .previous-success {
        color: #40c6c2;
    }

    .previous-danger {
        color: #fc6274;
    }

    /*--BLOQUE PACIENTE--*/

    .btn-error {
        background-color: #fc6274;
        border-color: #fc6274;
    }

    .btn-aqua {
        background-color: #40c6c2;
        border-color: #40c6c2;
    }

    .btn-purple {
        background-color: #b57dff;
        border-color: #b57dff;
    }

    .separator {
        font-size: 2rem;
    }

    .registryForm .flex-btn {
        width: 40% !important;
    }

    .flex-btn div {
        width: 100%;
    }

    .patient-btn {
        font-size: 1.3rem;
    }

    .title-container {
        flex: 0 0 25rem !important;
    }

    .title-container img {
        margin-top: -3px;
    }

    .title-container i {
        font-size: 2.5rem;
    }

    .arrow {
        font-size: 1.2rem;
    }

    /*--BLOQUE BÚSQUEDA Y AYUDA--*/

    .btn-separator {
        border: 1px solid vt-color(general-border);
        padding: 1% 0;
        margin: 0 1rem;
    }

    .separator {
        color: vt-color(card-border);
    }

    .sq-btn {
        padding: 0.5% 1.2%;
        transition: 0.2s;
        -webkit-appearance: none !important;
    }

    .sq-btn:hover {
        background-color: vt-color(light-primary);
    }

    .search-block a {
        font-size: 1.1rem;
    }

    .help-btn {
        color: #fc6274;
    }

    .help-btn i {
        font-size: 1.4rem;
    }

    .help-btn:hover {
        color: #80323b;
    }

    .registry-request-grid {
        width: 100%;
        height: calc(100vh - 140px);
    }

    .sr-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin: 0.5rem;
        flex-shrink: 0;
    }

    .sr-header-buttons {
        margin-right: 1.5em;
    }

    .collapsable-card-header {
        padding: 30px 20px !important;
        height: 60px;
    }

    .showing-collapse {
        padding: 30px 20px !important;
    }

    .title-container i {
        color: vt-color(primary) !important;
    }

    .estudio-footer .error {
        color: #fc6274 !important;
    }

    .estudio-footer .error:hover {
        color: vt-color(dark-primary) !important;
    }

    .main-col.show-prev {
        height: 65vh !important;
    }

    @media (max-width: 1370px) {

        .main-col.show-prev {
            height: 65vh !important;
        }

        .btn-pop {
            font-size: 1.1rem;
        }

        /*--TIMELINE--*/
        .timeline-item i {
            margin-top: 24%;
            font-size: 1rem;
        }

        .line-tl {
            margin-left: 23%;
        }

        /*--buttongroup--*/
        .buttongroup span {
            font-size: 0.9rem;
        }

        .buttongroup {
            top: 1.2rem;
        }

        /*--DATOS ESTUDIO--*/
        .estudio-footer a {
            font-size: 0.9rem;
        }

        .estudio-footer button {
            font-size: 0.9rem !important;
        }

        .form-registro label {
            font-size: 0.8rem;
        }

        .form-box div h5 {
            font-size: 1rem;
            margin-top: 0.6rem;
        }

        /*--PATIENT REQUEST DETAIL--*/
        .vt-data-block .vt-data-label {
            font-size: 0.7rem !important;
        }

        .vt-data-block .vt-data-value {
            font-size: 0.9rem !important;
        }

        .vt-data-block {
            line-height: 1.2;
        }

        /*--Tabs--AÑADIR EN LUGAR CORRESPONDIENTE--*/
        .vt-tabs .nav-tabs .nav-link,
        .vt-tabs--small .nav-tabs .nav-link {
            padding: 0.4rem 2rem;
            font-size: 0.8rem;
            letter-spacing: 0.1rem;
            font-weight: bold;
        }

        /*--BLOQUE ESTUDIO--*/
        .study-btns {
            margin-top: -8px;
            font-size: 1rem;
        }

        .study-btns .btn__icon .btn__icon--primary {
            font-size: 1.1rem;
        }

        /*--BLOQUE PACIENTE--*/
        .title-container {
            flex: 0 0 15rem !important;
        }

        .title-container img {
            margin-top: 1%;
        }

        .title-container i {
            font-size: 1.9rem;
        }

        .flex-btn {
            width: 38% !important;
        }

        h4 {
            font-size: 1rem;
        }

        small {
            font-size: 0.7rem;
        }

        .round-btn {
            border-radius: 50%;
            width: 25px;
            height: 25px;
            position: relative;
            margin: -5px 2% 0;
            padding: 1%;
        }

        .separator {
            font-size: 1.3rem;
        }

        .collapsable-card-header {
            padding: 25px 20px !important;
            height: 50px;
        }

        .showing-collapse {
            padding: 25px 20px !important;
        }

        .patient-btn {
            font-size: 1rem;
        }

        .round-btn {
            width: 23px;
            height: 23px;
            margin: -2px 2% 0;
            padding: 0;
        }

        .round-btn i {
            font-size: 1rem;
        }

        /*--BLOQUE SUPERIOR--*/
        .btn-separator {
            border: 1px solid vt-color(general-border);
            padding: 0 0;
            margin: 0 1rem;
        }

        .sq-btn {
            padding: 1%;
            transition: 0.2s;
        }

        .sq-btn:hover {
            background-color: vt-color(light-primary);
        }

        .search-block a {
            font-size: .9rem;
        }

        .help-btn {
            color: #fc6274;
        }

        .help-btn i {
            font-size: 1.4rem;
        }

        .help-btn:hover {
            color: #80323b;
        }
    }

    @media (max-width: 1187px) {
        .title-container {
            flex: 0 0 12rem !important;
        }

        .flex-btn {
            width: 40% !important;
        }
    }

    @media (max-width: 1400px) {
        .title-container {
            flex: 0 0 12rem !important;
        }

        .flex-btn {
            width: 40% !important;
        }

        .title-container {
            width: 30%;
        }
    }
</style>
